import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RenderedHtml } from 'modules/editor';
import { ReportQuestionButton } from 'modules/questionReports';
import { cn } from 'lib/utils';
import { AnswerReviewBody, hasAnswer, type AnswerReviewItem } from './AnswerReviewBody';

export interface ReviewListItem extends AnswerReviewItem {
  questionText: string;
  sectionId: string;
  sectionTitle: string;
  /** Marked for review by the learner; only present when the review is unlocked. */
  flagged?: boolean;
}

interface AnswerReviewListProps {
  answers: ReviewListItem[];
  attemptId?: string;
  correctAnswersHidden?: boolean;
  /** The card the learner is on (from the answer map, or the last one they touched). */
  focusIndex: number;
  onFocus: (index: number) => void;
  /** Accent of the result category: the focused card's ring and the active segment. */
  accent: string;
  className?: string;
}

type Filter = 'all' | 'wrong' | 'flagged';

/** "Reading · True / False" above a card's prompt. */
const TYPE_LABEL_KEY: Record<string, string> = {
  MULTIPLE_CHOICE: 'single_choice',
  MULTIPLE_RESPONSE: 'multiple_response',
  TRUE_FALSE: 'true_false',
  SHORT_ANSWER: 'short_answer',
};

const stateOf = (a: ReviewListItem): 'ok' | 'no' | 'skip' => (!hasAnswer(a.answer) ? 'skip' : a.isCorrect ? 'ok' : 'no');
const BADGE: Record<'ok' | 'no' | 'skip', string> = {
  ok: 'bg-[#E6F4EC] text-[#166534]',
  no: 'bg-[#FDECEC] text-[#B42318]',
  skip: 'bg-[#F1F4FB] text-[#646D84]',
};

function Segmented<T extends string>({ value, options, onChange, label, accent }: { value: T; options: Array<{ id: T; label: string }>; onChange: (v: T) => void; label: string; accent: string }) {
  return (
    // Many sections do not fit a phone: the strip scrolls sideways inside its own box instead of pushing the page wider.
    <div role="group" aria-label={label} className="flex max-w-full gap-1 overflow-x-auto rounded-xl bg-[#F1F4FB] p-1 [scrollbar-width:none] dark:bg-muted [&::-webkit-scrollbar]:hidden">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={value === option.id}
          onClick={() => onChange(option.id)}
          style={value === option.id ? { color: accent } : undefined}
          className={cn(
            'min-h-11 shrink-0 whitespace-nowrap rounded-[9px] px-3.5 py-2 text-[12.5px] font-bold transition-colors lg:min-h-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#10182B]',
            value === option.id ? 'bg-white shadow-[0_1px_2px_rgba(16,24,43,.12)] dark:bg-card' : 'text-[#4B5468] dark:text-muted-foreground',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Every answer of the attempt as a card, newest question state first in the learner's eyes: a coloured number, what
 * kind of question it was, the prompt, then the full review (answers, explanation, AI analysis or its lock). Filters
 * narrow the list to the wrong or marked ones, or to one section. A reading passage keeps its own split of passage and
 * questions inside its card.
 */
export function AnswerReviewList({ answers, attemptId, correctAnswersHidden, focusIndex, onFocus, accent, className }: AnswerReviewListProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  const [sectionId, setSectionId] = useState<string>('all');

  const sections = Array.from(new Map(answers.map((a) => [a.sectionId, a.sectionTitle])).entries());
  const wrongCount = answers.filter((a) => stateOf(a) !== 'ok').length;
  const flaggedCount = answers.filter((a) => a.flagged).length;

  const visible = answers
    .map((a, index) => ({ a, index }))
    .filter(({ a }) => (sectionId === 'all' || a.sectionId === sectionId) && (filter === 'all' || (filter === 'wrong' ? stateOf(a) !== 'ok' : a.flagged === true)));

  // The focused card might be filtered out (a tap on the answer map): show everything rather than a dead end.
  const focusHidden = focusIndex >= 0 && !visible.some(({ index }) => index === focusIndex);
  const shown = focusHidden && answers[focusIndex] ? answers.map((a, index) => ({ a, index })) : visible;

  return (
    <section className={className} aria-label={t('result.view.review_title')}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="font-display text-xl font-extrabold tracking-[-0.03em] text-foreground sm:text-[22px]">{t('result.view.review_title')}</h2>
        <div className="flex w-full min-w-0 flex-wrap gap-2.5 sm:w-auto">
          {sections.length > 1 && (
            <Segmented<string>
              label={t('result.view.review_section')}
              accent={accent}
              value={sectionId}
              onChange={setSectionId}
              options={[{ id: 'all', label: t('result.view.review_all_sections') }, ...sections.map(([id, title]) => ({ id, label: title || t('questions') }))]}
            />
          )}
          <Segmented<Filter>
            label={t('result.view.review_filter')}
            accent={accent}
            value={filter}
            onChange={setFilter}
            options={[
              { id: 'all' as const, label: t('result.view.review_all') },
              { id: 'wrong' as const, label: t('result.view.review_wrong', { count: wrongCount }) },
              ...(flaggedCount > 0 ? [{ id: 'flagged' as const, label: t('result.view.review_flagged', { count: flaggedCount }) }] : []),
            ]}
          />
        </div>
      </div>

      {shown.length === 0 && <p className="mt-6 text-sm text-muted-foreground">{t('result.view.review_empty')}</p>}
      <div className="mt-5 space-y-4">
        {shown.map(({ a, index }) => {
          const state = stateOf(a);
          const focused = index === focusIndex;
          const typeKey = TYPE_LABEL_KEY[a.questionType];
          const isPassage = a.questionType === 'PASSAGE';
          return (
            <article
              key={a.questionId ?? index}
              id={`review-q-${index + 1}`}
              aria-labelledby={`review-q-title-${index + 1}`}
              onFocusCapture={() => !focused && onFocus(index)}
              onClick={() => !focused && onFocus(index)}
              style={focused ? { borderColor: accent, boxShadow: `0 0 0 3px ${accent}22` } : undefined}
              className="scroll-mt-4 rounded-[22px] border border-[#E6E9F2] bg-card p-[18px] dark:border-border sm:px-6 sm:py-[22px]"
            >
              <div className="flex items-start gap-3">
                <span className={cn('grid h-[34px] min-w-[34px] place-items-center rounded-[10px] px-2 text-sm font-extrabold tabular-nums', BADGE[state])}>{index + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#646D84]">
                    {[a.sectionTitle, typeKey ? t(typeKey) : a.questionType.toLowerCase().replace(/_/g, ' '), state === 'skip' ? t('not_answered') : null].filter(Boolean).join(' · ')}
                  </div>
                  <div id={`review-q-title-${index + 1}`} className="mt-1.5 text-[15.5px] font-semibold leading-normal text-foreground">
                    {!isPassage && (a.questionText ? <RenderedHtml html={a.questionText} /> : t('question'))}
                  </div>
                </div>
              </div>

              <div className="mt-3">
                <AnswerReviewBody answer={a} correctAnswersHidden={correctAnswersHidden} attemptId={attemptId} />
              </div>

              {/* The learner's own review only: the API refuses (and the button hides) for anyone else's attempt. */}
              {attemptId && a.questionId && (
                <div className="mt-4 flex justify-end">
                  <ReportQuestionButton attemptId={attemptId} questionId={a.questionId} withLabel />
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
